import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { headerdata } from "../Data/AllMappingData";
import { getToken, RESTRICTED_STAFF_ID } from "../Utils/util";
import Logo from "../img/Logo.png";
import { dashboardIcon, menuImgIcon } from "../img";
import { IoIosArrowUp, IoIosArrowDown } from "react-icons/io";
import { cx } from "../Utils/hotel/utils";

function SideBar({ isOpen, setIsOpen }) {
	const location = useLocation();
	const [activeSubmenu, setActiveSubmenu] = useState(null);
	const [openSubmenus, setOpenSubmenus] = useState([]); // replaces activeSubmenuKey
	const Userdata = getToken();
	const userAccess = Userdata?.access || {};
	const userRole = Userdata?.role;
	const storedStaffId =
		typeof window !== "undefined" ? localStorage.getItem("staffId") : null;
	const resolvedStaffId = storedStaffId || Userdata?.staffId || "";
	const isRestrictedStaff = resolvedStaffId === RESTRICTED_STAFF_ID;
	const filteredMenuItems = useMemo(() => {
		if (isRestrictedStaff) {
			return headerdata.filter((data) => data.pagename === "Blog" || data.pagename === "Add Item" || data.pagename === "Sitemap");
		}
		return headerdata;
	}, [isRestrictedStaff]);

	useEffect(() => {
		const matchPath = (path, otherpaths) => {
			const { pathname, search } = location;
			const fullPath = `${pathname}${search}`;
			if (fullPath === path) return true;
			if (otherpaths) {
				return otherpaths.some((keyword) => fullPath.includes(keyword));
			}
			return false;
		};

		headerdata.forEach((data) => {
			if (data.submenu) {
				data.submenu.forEach((subItem) => {
					if (matchPath(subItem.pathname, subItem.otherpaths)) {
						setActiveSubmenu(data.id);
					}
				});
			}
		});
	}, [location]);

	const toggleSubmenu = (key, parentId = "") => {
		const keyStr = String(key);
		const parentIdStr = String(parentId);
		setOpenSubmenus((prev) => {
			const levelPrefix =
				parentIdStr && parentIdStr !== "undefined" && parentIdStr !== "null"
					? `${parentIdStr}-`
					: "";
			const filtered = prev.filter(
				(k) => typeof k === "string" && (!k.startsWith(levelPrefix) || k === keyStr)
			);
			if (prev.includes(keyStr)) {
				// Close this submenu
				return filtered.filter((k) => k !== keyStr);
			} else {
				// Open this submenu, close others at this level
				return [...filtered, keyStr];
			}
		});
	};

	const isSubmenuOpen = (key) => openSubmenus.includes(String(key));

	const isActive = (path, otherpaths) => {
		const { pathname, search } = location;
		const fullPath = `${pathname}${search}`;
		return (
			fullPath === path ||
			(otherpaths && otherpaths.some((keyword) => fullPath.includes(keyword)))
		);
	};

	const hasAccess = (keys) => {
		return keys.some((key) => userAccess[key] === 1);
	};

	// Recursive submenu renderer
	const renderSubMenu = (submenu, parentId = "") => (
		<ul className="ml-9 mt-2">
			{submenu
				.filter((subItem) => {
					if (userRole === 1) return true;
					return hasAccess(subItem.accessKeys || []);
				})
				.map((subItem) => {
					const hasNested = !!subItem.submenu;
					const subMenuKey = `${parentId}-${subItem.id}`;
					return (
						<li key={subMenuKey} className="border-l pl-5 pb-1">
							{hasNested ? (
								<>
									<div
										className="flex items-center cursor-pointer text-[13px] font-semibold"
										onClick={() => toggleSubmenu(subMenuKey, parentId)}
									>
										<span>{subItem.name}</span>
										<span className="ml-2">
											{isSubmenuOpen(subMenuKey) ? (
												<IoIosArrowUp />
											) : (
												<IoIosArrowDown />
											)}
										</span>
									</div>
									{isSubmenuOpen(subMenuKey) &&
										renderSubMenu(subItem.submenu, subMenuKey)}
								</>
							) : (
								<Link
									to={subItem.pathname}
									onClick={() => setIsOpen?.(false)}
									className={`${
										isActive(subItem.pathname, subItem.otherpaths)
											? "text-primary"
											: "text-thirdColor"
									} hover:text-primary text-[13px]`}
								>
									{subItem.name}
								</Link>
							)}
						</li>
					);
				})}
		</ul>
	);

	return (
		<>
			{/* Overlay for mobile */}
			{isOpen && (
				<div
					className="fixed inset-0 bg-black bg-opacity-50 lg:hidden z-40"
					onClick={() => setIsOpen?.(false)}
				/>
			)}

			<aside
				className={`fixed top-0 left-0 h-screen w-[220px] border-r border-gray-200 bg-white flex flex-col justify-between transform transition-transform duration-300 ease-in-out lg:translate-x-0 z-40 ${
					isOpen ? "translate-x-0" : "-translate-x-full"
				}`}
			>
				{/* Header / Logo */}
				<div className="pt-5 pb-3 px-3 flex-shrink-0 bg-white">
					<Link to="/" onClick={() => setIsOpen?.(false)}>
						<img src={Logo} alt="Logo" className="w-3/4 mx-auto" />
					</Link>
				</div>

				{/* Middle Navigation Menu */}
				<div className="flex-1 overflow-y-auto scrollbar-hide px-0 py-1">
					<ul className="flex flex-col gap-px">
						<p className="text-[12px] mb-2 ml-3">MANAGEMENT</p>
						{!isRestrictedStaff && (
							<Link
								to="/"
								onClick={() => setIsOpen?.(false)}
								className={`bg-primary text-white h-12 flex items-center justify-start tracking-wide text-sm transition duration-500 rounded-lg mx-3`}
							>
								<li className="flex items-center gap-3 pl-4 font-semibold text-md">
									<img src={dashboardIcon} alt="Dashboard" />
									<span>Dashboard</span>
								</li>
							</Link>
						)}

						{!isRestrictedStaff &&
							(userRole === 1 || userAccess["hotel_dashboard"] === 1) && (
							<Link
								to="/hotel/dashboard"
								onClick={() => setIsOpen?.(false)}
								className={`bg-primary text-white h-12 flex items-center justify-start tracking-wide text-sm transition duration-500 rounded-lg mx-3 mt-2`}
							>
								<li className="flex items-center gap-3 pl-4 font-semibold text-md">
									<img src={dashboardIcon} alt="Dashboard" />
									<span>Hotel Dashboard</span>
								</li>
							</Link>
						)}

						{Userdata &&
							filteredMenuItems
								?.filter((data) => {
									if (userRole === 1) return true;
									if (userRole === 2 && data.accessKeys) {
										return hasAccess(data.accessKeys);
									}
									return !data.accessKeys;
								})
								.map((data) => (
									<div key={data.id}>
										<Link
											to={data.pathname}
											className={`${
												isActive(data.pathname, data.otherpaths) &&
												data.pathname !== "/"
													? "text-primary"
													: "text-thirdColor hover:text-primary"
											} my-3 flex items-center justify-start tracking-wide text-sm transition duration-500 rounded-lg mx-3`}
											onClick={(e) => {
												if (data.submenu) {
													e.preventDefault();
													toggleSubmenu(data.id, "");
												}
												if (!data.submenu) setIsOpen?.(false);
											}}
										>
											<li className="flex items-center gap-3 pl-4 font-semibold text-md justify-between w-full">
												<div className="flex items-center gap-3">
													<div className="text-lg">{data.img}</div>
													<span>{data.pagename}</span>
												</div>
												{data.submenu && (
													<span>
														{isSubmenuOpen(data.id) ? (
															<IoIosArrowUp />
														) : (
															<IoIosArrowDown />
														)}
													</span>
												)}
											</li>
										</Link>
										{isSubmenuOpen(data.id) && data.submenu && renderSubMenu(data.submenu, data.id)}
									</div>
								))}
					</ul>

					{/* Image after last menu option */}
					<div className="p-3 mt-auto">
						<img src={menuImgIcon} width={500} height={500} alt="footer graphic" className="w-full" />
					</div>
				</div>

				{/* Bottom Footer / Profile */}
				<div className="flex-shrink-0 p-3 bg-white border-t border-gray-100 z-10">
					<div className="flex items-center rounded-lg gap-2">
						<div className="flex-1 min-w-0">
							<h4 className="text-base font-medium text-gray-800 truncate">
								{Userdata?.email}
							</h4>
							<p className="text-sm text-gray-500 truncate">{Userdata?.name}</p>
						</div>
					</div>
				</div>
			</aside>
		</>
	);
}

export default SideBar;
